import { useEffect, useRef, useState, type ReactNode } from 'react'
import { PanelLeftClose, PanelLeft } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { SearchInput } from '../SearchInput'

export interface MasterDetailItem {
  id: string
  /** Primary identifier, usually monospace (SO#, PO#, Lot#). */
  primary: ReactNode
  /** Secondary label (customer name, product, etc.). */
  secondary?: ReactNode
  /** Terse metadata (date, status chip line). */
  meta?: ReactNode
}

export interface MasterDetailTab {
  id: string
  label: string
}

export interface MasterDetailProps<T extends MasterDetailItem> {
  items: T[]
  selectedId?: string
  onSelect: (id: string) => void

  /** Render the detail for the selected item. */
  renderDetail: (item: T) => ReactNode

  /** Optional tabs inside the detail pane. */
  tabs?: MasterDetailTab[]
  activeTabId?: string
  onTabChange?: (id: string) => void

  /** Search over the list. Called on every change (debounce upstream). */
  onSearch?: (query: string) => void
  searchPlaceholder?: string
  /** Controlled search box text. Omit to leave the box uncontrolled
   *  (existing behavior). Needed when a parent applies a saved search and
   *  the visible box must reflect it, not just the underlying query. */
  searchValue?: string

  /** Extra toolbar content rendered between the search box and the
   *  collapse toggle (e.g. a saved-searches menu). Hidden when collapsed. */
  toolbarExtra?: ReactNode

  /** Detail header — shown above tabs/content. */
  renderDetailHeader?: (item: T) => ReactNode

  /** Auto-select the first item on mount if none is selected. Default true. */
  autoSelectFirst?: boolean

  className?: string
}

/**
 * Spec §Archetype 3. Inspect one record at a time from a scrollable list.
 * Default 320px list + detail content; list collapses to a 32px icon strip
 * (`Cmd+\`). Keyboard `j`/`k` advances selection; detail updates.
 *
 * Deep-linking (URL → `selectedId`) is handled by the module's page-level
 * route — this primitive just renders whatever the parent selects.
 */
export function MasterDetail<T extends MasterDetailItem>({
  items,
  selectedId,
  onSelect,
  renderDetail,
  tabs,
  activeTabId,
  onTabChange,
  onSearch,
  searchPlaceholder = 'Search',
  searchValue,
  toolbarExtra,
  renderDetailHeader,
  autoSelectFirst = true,
  className,
}: MasterDetailProps<T>) {
  const [collapsed, setCollapsed] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (autoSelectFirst && !selectedId && items[0]) {
      onSelect(items[0].id)
    }
    // Select-first is one-shot on mount / data arrival — intentionally
    // excludes `onSelect` from deps to avoid re-firing if the parent
    // replaces the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectFirst, items.length])

  // Scroll the selected row into view whenever selection changes.
  // `block: 'nearest'` is a no-op when the row is already visible (a
  // normal click, or autoSelectFirst landing on row 0) — this only does
  // something for the case that was previously silently broken: a
  // programmatic/deep-link selection (e.g. Customer Pricelists' `?partner=`
  // resolution) landing on a row further down than the current scroll
  // position. Without this, the detail pane updates correctly but the
  // list looks unchanged, reading as "this didn't go anywhere" even
  // though the right record was already selected — see the "View
  // pricelist" cross-link investigation.
  useEffect(() => {
    selectedItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selectedId])

  const selectedItem = items.find((i) => i.id === selectedId)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'j' && e.key !== 'k') return
    if (!items.length) return
    e.preventDefault()
    const currentIdx = selectedItem ? items.indexOf(selectedItem) : -1
    const nextIdx =
      e.key === 'j'
        ? Math.min(items.length - 1, currentIdx + 1)
        : Math.max(0, currentIdx - 1)
    const next = items[nextIdx]
    if (next) onSelect(next.id)
  }

  return (
    <div
      className={cn(
        // h-full + min-h-[520px]: take the parent's bounded height so the
        // flex chain inside (the section's flex-1 overflow-y-auto slot)
        // can compute remaining space and scroll when content overflows.
        // Without h-full the grid sized to content and the detail slot
        // never engaged its scrollbar — Sprint 08 surfaced this when
        // formulations rendered all 16 ingredient rows inline + Notes +
        // Procedure, exceeding the viewport. Customer/Vendor Pricelists
        // worked by accident because EditableGrid's 70vh max kept the
        // detail content small.
        'grid h-full min-h-[520px] overflow-hidden rounded-md border border-border-subtle bg-surface',
        className,
      )}
      style={{
        gridTemplateColumns: collapsed
          ? 'var(--master-detail-list-collapsed) 1fr'
          : 'var(--master-detail-list-width) 1fr',
      }}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Master detail"
      tabIndex={0}
    >
      <aside
        ref={listRef}
        className={cn(
          // `min-h-0 overflow-hidden` mirrors the detail <section>
          // pattern below so the inner list div's flex-1 +
          // overflow-y-auto can engage. CSS Grid children default to
          // min-height: auto, which lets the aside grow to content
          // size when the list has many items (Customer Pricelists
          // showed this on 41 rows — list extended below the
          // viewport with no scrollbar). Bug captured by Chris
          // 2026-04-26 as work-blocking; affects every consumer of
          // the primitive.
          'flex min-h-0 flex-col overflow-hidden border-r border-border-subtle bg-surface-sunk',
          collapsed && 'items-center',
        )}
      >
        <header className="flex items-center gap-1 border-b border-border-subtle p-2">
          {!collapsed && onSearch ? (
            <SearchInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              containerClassName="flex-1 max-w-none"
            />
          ) : null}
          {!collapsed ? toolbarExtra : null}
          <button
            type="button"
            aria-label={collapsed ? 'Expand list' : 'Collapse list'}
            onClick={() => setCollapsed((v) => !v)}
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
          >
            {collapsed ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
          </button>
        </header>
        {!collapsed ? (
          <div className="flex-1 overflow-y-auto" role="listbox" aria-label="Records">
            {items.map((item) => {
              const isSelected = item.id === selectedId
              return (
                <button
                  key={item.id}
                  ref={isSelected ? selectedItemRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    'flex w-full cursor-pointer flex-col items-stretch gap-0.5 border-b border-border-subtle border-l-2 border-l-transparent px-3 py-2.5 text-left transition-colors duration-fast',
                    'hover:bg-surface-hover',
                    isSelected && 'border-l-accent bg-accent-subtle',
                  )}
                >
                  <span className="text-role-data-primary">{item.primary}</span>
                  {item.secondary ? (
                    <span className="truncate text-sm font-medium text-primary">
                      {item.secondary}
                    </span>
                  ) : null}
                  {item.meta ? (
                    <span className="text-role-metadata flex items-center gap-2">
                      {item.meta}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : null}
      </aside>

      <section className="flex min-w-0 flex-col overflow-hidden">
        {selectedItem && renderDetailHeader ? (
          <header className="flex items-start gap-3 border-b border-border-subtle bg-surface px-5 py-3">
            {renderDetailHeader(selectedItem)}
          </header>
        ) : null}
        {tabs?.length ? (
          <div
            role="tablist"
            className="flex gap-1 border-b border-border-subtle bg-surface px-5"
          >
            {tabs.map((tab) => {
              const active = tab.id === activeTabId
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onTabChange?.(tab.id)}
                  className={cn(
                    '-mb-px cursor-pointer border-0 border-b-2 border-transparent bg-transparent px-3 py-2.5 text-sm font-medium text-secondary transition-colors duration-fast',
                    'hover:text-primary',
                    active && 'border-b-accent text-accent-text',
                  )}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        ) : null}
        <div className="flex-1 overflow-y-auto bg-surface p-5">
          {selectedItem ? (
            renderDetail(selectedItem)
          ) : (
            <p className="text-sm text-tertiary">Select a record to see detail.</p>
          )}
        </div>
      </section>
    </div>
  )
}
